import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePreVisitContactDto,
  PreVisitContactDetail,
  UpdatePreVisitContactDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';

/** Stage 2 pre-visit contacts — every mutation returns the file's full list. */
export async function listPreVisitContacts(fileId: string): Promise<PreVisitContactDetail[]> {
  const { data } = await api.get<PreVisitContactDetail[]>(`/files/${fileId}/pre-visit-contacts`);
  return data;
}

export const preVisitContactKeys = {
  byFile: (fileId: string) => ['pre-visit-contacts', fileId] as const,
};

export function usePreVisitContacts(fileId: string) {
  return useQuery({
    queryKey: preVisitContactKeys.byFile(fileId),
    queryFn: () => listPreVisitContacts(fileId),
    enabled: !!fileId,
  });
}

/**
 * Unlike the Step 5.3 service records, writing a contact does not re-mirror
 * anything onto the file and starts no clock — so only this list is refreshed.
 * Invalidating the file query here would refetch it on every keystroke-sized
 * save for no change.
 */
function usePreVisitContactMutation<TVars>(
  fileId: string,
  fn: (vars: TVars) => Promise<PreVisitContactDetail[]>,
) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: (contacts) => {
      qc.setQueryData(preVisitContactKeys.byFile(fileId), contacts);
      toast.success('gov.stage2.contacts.savedToast');
    },
    onError: (err) => toast.error(err, 'gov.stage2.contacts.saveError'),
  });
}

export function useCreatePreVisitContact(fileId: string) {
  return usePreVisitContactMutation(fileId, async (dto: CreatePreVisitContactDto) => {
    const { data } = await api.post<PreVisitContactDetail[]>(
      `/files/${fileId}/pre-visit-contacts`,
      dto,
    );
    return data;
  });
}

export function useUpdatePreVisitContact(fileId: string) {
  return usePreVisitContactMutation(
    fileId,
    async ({ id, dto }: { id: string; dto: UpdatePreVisitContactDto }) => {
      const { data } = await api.patch<PreVisitContactDetail[]>(
        `/files/${fileId}/pre-visit-contacts/${id}`,
        dto,
      );
      return data;
    },
  );
}

export function useDeletePreVisitContact(fileId: string) {
  return usePreVisitContactMutation(fileId, async (id: string) => {
    const { data } = await api.delete<PreVisitContactDetail[]>(
      `/files/${fileId}/pre-visit-contacts/${id}`,
    );
    return data;
  });
}
